import classNames from 'classnames';
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import './index.scss';

type AlignType = 'start' | 'center' | 'end';
type PlacementType = 'bottom' | 'top' | 'left' | 'right';
type TriggerType = 'click' | 'hover';

export interface IDropdown {
  children: ReactNode;
  width?: number | string;
  placement?: PlacementType;
  align?: AlignType;
  open?: boolean; // 受控也可内部处理
  onOpen?: (open: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
  offsetData?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  disabled?: boolean; // 是否禁用下拉
  documentClickClose?: boolean; // 是否点击文档其他地方关闭下拉
  dropdownPortalClassName?: string
  matchTriggerWidth?: boolean; // 是否和触发器宽度一致
  trigger?: TriggerType;
  hoverDelay?: number;
}

interface TriggerProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface ContentProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface DropdownContextType {
  open: boolean;
  toggleDropdown: () => void;
  openDropdown: () => void;
  closeDropdown: () => void;
  triggerRef: React.RefObject<HTMLDivElement> | null;
  trigger: TriggerType;
}

/**
 * 锁定和解锁页面滚动
 */
const lockPageScroll = (lock: boolean) => {
  const lockScrollClass = 'odc-lock-scroll';
  if (lock) {
    document.querySelector('html')?.classList.add(lockScrollClass);
  } else {
    document.querySelector('html')?.classList.remove(lockScrollClass);
  }
};

const DropdownContext = createContext<DropdownContextType>({
  open: false,
  toggleDropdown: () => { },
  openDropdown: () => { },
  closeDropdown: () => { },
  triggerRef: null,
  trigger: 'click',
});

/**
 * 下拉选择触发器
 */
const Trigger: React.FC<TriggerProps> = ({ children, className = '', style = {} }) => {
  const { toggleDropdown, openDropdown, closeDropdown, triggerRef, open, trigger } = useContext(DropdownContext);

  const handleClick = useCallback(() => {
    if (trigger === 'click') {
      toggleDropdown();
    }
  }, [trigger, toggleDropdown]);

  const handleMouseEnter = useCallback(() => {
    if (trigger === 'hover') {
      openDropdown();
    }
  }, [trigger, openDropdown]);

  const handleMouseLeave = useCallback(() => {
    if (trigger === 'hover') {
      closeDropdown();
    }
  }, [trigger, closeDropdown]);

  return (
    <div
      className={classNames('odc-dropdown-trigger', className, {
        'odc-dropdown-open': open,
      })}
      style={style}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={triggerRef}
    >
      {children}
    </div>
  );
};

/**
 * 下拉选择内容
 */
const Content: React.FC<ContentProps> = ({ children, className = '', style = {} }) => {
  return (
    <div className={classNames('odc-dropdown-content', className)} style={style}>
      {children}
    </div>
  );
};

const Dropdown: React.FC<IDropdown> & {
  Trigger: typeof Trigger;
  Content: typeof Content;
} = (props) => {
  const {
    children,
    width,
    placement: propPlacement = 'bottom',
    align = 'start',
    open: propOpen,
    onOpen,
    className = '',
    style = {},
    disabled = false,
    documentClickClose = true,
    dropdownPortalClassName,
    offsetData = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    matchTriggerWidth = false,
    trigger = 'click',
    hoverDelay = 150,
  } = props;
  const [open, setOpen] = useState(propOpen || false);
  const [actualPlacement, setActualPlacement] = useState<PlacementType>(propPlacement);
  const [actualAlign, setActualAlign] = useState<AlignType>(align);
  const [mounted, setMounted] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);
  const triggerDropdownDistance = 12;

  /**
   * 获取水平方向的位置
   */
  const getHorizontalPosition = useCallback((
    triggerRect: DOMRect,
    align: AlignType,
    dropdownEl: HTMLElement,
  ): string => {
    const dropdownWidth = dropdownEl.offsetWidth;

    switch (align) {
      case 'start':
        return `${triggerRect.left + window.scrollX}px`;
      case 'end':
        return `${triggerRect.right + window.scrollX - dropdownWidth}px`;
      case 'center':
        return `${triggerRect.left + window.scrollX + (triggerRect.width / 2) - (dropdownWidth / 2)}px`;
      default:
        return `${triggerRect.left + window.scrollX}px`;
    }
  }, []);

  /**
   * 获取垂直方向的位置
   */
  const getVerticalPosition = useCallback((
    triggerRect: DOMRect,
    align: AlignType,
    dropdownEl: HTMLElement,
  ): string => {
    const dropdownHeight = dropdownEl.offsetHeight;

    switch (align) {
      case 'start':
        return `${triggerRect.top + window.scrollY}px`;
      case 'end':
        return `${triggerRect.bottom + window.scrollY - dropdownHeight}px`;
      case 'center':
        return `${triggerRect.top + window.scrollY + (triggerRect.height / 2) - (dropdownHeight / 2)}px`;
      default:
        return `${triggerRect.top + window.scrollY}px`;
    }
  }, []);

  /**
   * 计算下拉框最佳展开方向和对齐方式，避免超出视口
   */
  const calcPosition = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return;
    if (dropdownRef.current.offsetWidth === 0 || dropdownRef.current.offsetHeight === 0) {
      // 如果尺寸为0，可能是因为元素尚未完全渲染，等待下一帧
      requestAnimationFrame(calcPosition);
      return;
    }
    const { top = 0, left = 0, right = 0, bottom = 0 } = offsetData || {};
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 计算各方向的可用空间
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceTop = triggerRect.top;
    const spaceRight = viewportWidth - triggerRect.right;
    const spaceLeft = triggerRect.left;

    // 计算最佳展开方向
    let bestPlacement = propPlacement;
    if (propPlacement === 'bottom' && spaceBottom < dropdownRect.height && spaceTop > dropdownRect.height) {
      bestPlacement = 'top';
    } else if (propPlacement === 'top' && spaceTop < dropdownRect.height && spaceBottom > dropdownRect.height) {
      bestPlacement = 'bottom';
    } else if (propPlacement === 'left' && spaceLeft < dropdownRect.width && spaceRight > dropdownRect.width) {
      bestPlacement = 'right';
    } else if (propPlacement === 'right' && spaceRight < dropdownRect.width && spaceLeft > dropdownRect.width) {
      bestPlacement = 'left';
    }

    // 计算对齐方式，避免超出视口
    let bestAlign = align;
    if (bestPlacement === 'bottom' || bestPlacement === 'top') {
      const leftPos = triggerRect.left;
      const rightSpace = viewportWidth - (leftPos + dropdownRect.width);

      if (align === 'start' && rightSpace < 0) {
        bestAlign = 'end';
      } else if (align === 'end' && leftPos + triggerRect.width - dropdownRect.width < 0) {
        bestAlign = 'start';
      } else if (align === 'center') {
        const centerPos = leftPos + triggerRect.width / 2 - dropdownRect.width / 2;
        if (centerPos < 0) {
          bestAlign = 'start';
        } else if (centerPos + dropdownRect.width > viewportWidth) {
          bestAlign = 'end';
        }
      }
    } else if (bestPlacement === 'left' || bestPlacement === 'right') {
      const topPos = triggerRect.top;
      const bottomSpace = viewportHeight - (topPos + dropdownRect.height);

      if (align === 'start' && bottomSpace < 0) {
        bestAlign = 'end';
      } else if (align === 'end' && topPos + triggerRect.height - dropdownRect.height < 0) {
        bestAlign = 'start';
      } else if (align === 'center') {
        const centerPos = topPos + triggerRect.height / 2 - dropdownRect.height / 2;
        if (centerPos < 0) {
          bestAlign = 'start';
        } else if (centerPos + dropdownRect.height > viewportHeight) {
          bestAlign = 'end';
        }
      }
    }

    setActualPlacement(bestPlacement);
    setActualAlign(bestAlign);

    // 更新下拉框位置
    if (dropdownRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownStyle = dropdownRef.current.style;

      // 根据方向设置位置
      switch (bestPlacement) {
        case 'bottom':
          dropdownStyle.top = `${triggerRect.bottom + window.scrollY + top + triggerDropdownDistance}px`;
          dropdownStyle.bottom = 'auto';
          dropdownStyle.left = getHorizontalPosition(triggerRect, bestAlign, dropdownRef.current);
          dropdownStyle.right = 'auto';
          break;
        case 'top':
          dropdownStyle.bottom = 'auto';
          dropdownStyle.top = `${triggerRect.top + window.scrollY + bottom - dropdownRef.current.offsetHeight - triggerDropdownDistance}px`;
          dropdownStyle.left = getHorizontalPosition(triggerRect, bestAlign, dropdownRef.current);
          dropdownStyle.right = 'auto';
          break;
        case 'left':
          dropdownStyle.right = 'auto';
          dropdownStyle.left = `${triggerRect.left + window.scrollX + left - dropdownRef.current.offsetWidth - triggerDropdownDistance}px`;
          dropdownStyle.top = getVerticalPosition(triggerRect, bestAlign, dropdownRef.current);
          dropdownStyle.bottom = 'auto';
          break;
        case 'right':
          dropdownStyle.left = `${triggerRect.right + window.scrollX + right + triggerDropdownDistance}px`;
          dropdownStyle.right = 'auto';
          dropdownStyle.top = getVerticalPosition(triggerRect, bestAlign, dropdownRef.current);
          dropdownStyle.bottom = 'auto';
          break;
      }
      // 设置宽度
      if (width) {
        dropdownStyle.width = typeof width === 'number' ? `${width}px` : width;
      } else {
        dropdownStyle.width = contentWidth ? `${contentWidth}px` : 'auto';
      }
    }
  }, [propPlacement, align, offsetData, width, contentWidth, triggerDropdownDistance, getHorizontalPosition, getVerticalPosition]);

  /**
   * 如果matchTriggerWidth为true，设置内容宽度与触发器一致
   */
  const adjustContentWidth = useCallback(() => {
    if (matchTriggerWidth && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setContentWidth(triggerRect.width);
    }
  }, [matchTriggerWidth]);

  useEffect(() => {
    portalContainerRef.current = document.createElement('div');
    portalContainerRef.current.className = 'odc-dropdown-portal';
    document.body.appendChild(portalContainerRef.current);
    setMounted(true);
    adjustContentWidth();
    return () => {
      if (portalContainerRef.current) {
        document.body.removeChild(portalContainerRef.current);
      }
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [adjustContentWidth]);

  useEffect(() => {
    if (propOpen !== undefined) {
      setOpen(propOpen);
    }
  }, [propOpen]);

  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node) &&
        open &&
        (propOpen === undefined || documentClickClose)
      ) {
        setOpen(false);
        onOpen?.(false);
      }
    };

    if (open) {
      if (isInitialRender) {
        setIsInitialRender(false);
        requestAnimationFrame(() => {
          calcPosition();
          requestAnimationFrame(() => {
            calcPosition();
          });
        });
      } else {
        calcPosition();
      }
      if (trigger === 'click') {
        document.addEventListener('mousedown', handleClickOutside);
      }
      window.addEventListener('resize', calcPosition);
      window.addEventListener('resize', adjustContentWidth);
    }
    lockPageScroll(open);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calcPosition);
      window.removeEventListener('resize', adjustContentWidth);
    };
  }, [open, onOpen, isInitialRender, documentClickClose, propOpen, trigger, adjustContentWidth, calcPosition]);

  /**
             * 下拉框打开/关闭切换
             */
  const toggleDropdown = () => {
    if (disabled) return;
    const currentOpen = !open;
    if (propOpen !== undefined) {
      onOpen?.(currentOpen);
    } else {
      setOpen(currentOpen);
      onOpen?.(currentOpen);
    }
  };

  /**
             * 打开下拉框
             */
  const openDropdown = () => {
    if (disabled) return;
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setOpen(true);
    onOpen?.(true);
  };

  /**
             * 关闭下拉框,用于 hover 模式
             */
  const closeDropdown = () => {
    if (disabled) return;
    if (trigger === 'hover') {
      hoverTimeoutRef.current = window.setTimeout(() => {
        setOpen(false);
        onOpen?.(false);
      }, hoverDelay);
    } else {
      setOpen(false);
      onOpen?.(false);
    }
  };

  /**
             * 分离 Trigger、Content 和其他内容
             */
  const childrenArray = React.Children.toArray(children);
  const triggerElement = childrenArray.find(
    child => React.isValidElement(child) && child.type === Trigger,
  );

  const contentElement = childrenArray.find(
    child => React.isValidElement(child) && child.type === Content,
  );

  const otherElements = childrenArray.filter(
    child => !React.isValidElement(child) || (child.type !== Trigger && child.type !== Content),
  );

  /**
             * 创建共享的上下文
             */
  const contextValue: DropdownContextType = {
    open,
    toggleDropdown,
    openDropdown,
    closeDropdown,
    triggerRef,
    trigger,
  };

  /**
             * 处理下拉框的 hover 事件
             */
  const handleDropdownMouseEnter = () => {
    if (trigger === 'hover' && hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    if (trigger === 'hover') {
      closeDropdown();
    }
  };

  const renderDropdown = () => {
    if (!mounted || !portalContainerRef.current) return null;
    return ReactDOM.createPortal(
      <div
        className={classNames(
          'odc-dropdown',
          `odc-dropdown-placement-${actualPlacement}`,
          `odc-dropdown-align-${actualAlign}`,
          {
            'odc-dropdown-open': open && !isInitialRender,
            'odc-dropdown-initial': isInitialRender && open,
          },
          dropdownPortalClassName,
        )}
        ref={(node) => {
          // @ts-ignore
          dropdownRef.current = node;
          // 测量内容宽度
          if (node && open && !contentWidth) {
            const rect = node.getBoundingClientRect();
            if (rect.width > 0) {
              setContentWidth(rect.width);
            }
          }
        }}
        style={{
          width: width ? (typeof width === 'number' ? `${width}px` : width)
            : contentWidth ? `${contentWidth}px` : 'auto',
        }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        {contentElement || otherElements}
      </div>,
      portalContainerRef.current,
    );
  };

  return (
    <DropdownContext.Provider value={contextValue}>
      <div
        className={classNames('odc-dropdown-container', className)}
        style={style}
        ref={containerRef}
      >
        {triggerElement}
        {renderDropdown()}
      </div>
    </DropdownContext.Provider>
  );
};
const MemoContent = React.memo(Content);
Dropdown.Trigger = Trigger;
Dropdown.Content = MemoContent;

export default Dropdown;